import QRCode from "qrcode";
import { resolveMedia, rescuePublicUrl, isUnverified } from "./media";

const S = 1080;
const DISPLAY = "Chivo, 'IBM Plex Sans', Arial, sans-serif";

const loadImg = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

function drawCover(ctx, img, x, y, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale, sh = h / scale;
  const sx = (img.width - sw) / 2;
  // Tall screenshots (social posts) usually have the animals at the bottom.
  const sy = Math.max(0, (img.height - sh) * (img.height / img.width > 1.6 ? 0.92 : 0.3));
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function wrap(ctx, text, maxW, maxLines) {
  const words = (text || "").split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1].replace(/\s*\S*$/, "") + "…";
    return kept;
  }
  return lines;
}

function fitFont(ctx, text, weight, start, family, maxW) {
  let size = start;
  do { ctx.font = `${weight} ${size}px ${family}`; size -= 1; } while (ctx.measureText(text).width > maxW && size > 9);
}

// Builds a branded 1080x1080 PNG for an urgent rescue alert. Returns a Blob.
export async function generateShareCard(r) {
  if (document.fonts?.ready) await document.fonts.ready;
  const url = rescuePublicUrl(r.id);
  const [photo, logo, qr] = await Promise.all([
    loadImg(resolveMedia(r.photo_url)),
    loadImg("/brand/paws-on-record-logo-512.jpg"),
    QRCode.toDataURL(url, { margin: 1, width: 300 }).then(loadImg),
  ]);
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, S, S);

  ctx.fillStyle = "#dc2626"; ctx.fillRect(0, 0, S, 110);
  ctx.fillStyle = "#fff"; ctx.font = `900 50px ${DISPLAY}`; ctx.textBaseline = "middle";
  ctx.fillText("URGENT RESCUE CAMPAIGN", 60, 57);
  ctx.font = `700 24px ${DISPLAY}`; ctx.textAlign = "right";
  ctx.fillText((r.status || "Needs Help").toUpperCase(), S - 60, 57);
  ctx.textAlign = "left";

  if (photo) drawCover(ctx, photo, 0, 110, S, 540);
  else {
    ctx.fillStyle = "#18181b"; ctx.fillRect(0, 110, S, 540);
    if (logo) ctx.drawImage(logo, S / 2 - 170, 210, 340, 340);
  }

  if (isUnverified(r)) {
    ctx.fillStyle = "#f59e0b"; ctx.fillRect(0, 596, S, 54);
    ctx.fillStyle = "#111"; ctx.font = `800 24px ${DISPLAY}`;
    ctx.fillText("REPORTED INCIDENT · CURRENT RESCUE STATUS UNCONFIRMED", 60, 624);
  }

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#fff"; ctx.font = `900 50px ${DISPLAY}`;
  const titleLines = wrap(ctx, r.title, S - 120, 2);
  titleLines.forEach((l, i) => ctx.fillText(l, 60, 712 + i * 56));
  let y = 712 + titleLines.length * 56 + 4;
  ctx.fillStyle = "#a1a1aa";
  const where = [isUnverified(r) ? `Reported location: ${r.incident_location || r.city}` : r.incident_location || `${r.city}, ${r.state_code || r.state}`, r.incident_date && `Reported ${r.incident_date}`]
    .filter(Boolean).join(" · ");
  fitFont(ctx, where, 600, 26, DISPLAY, S - 120);
  ctx.fillText(where, 60, y);
  y += 40;
  ctx.fillStyle = "#f87171"; ctx.font = `800 28px ${DISPLAY}`;
  ctx.fillText(wrap(ctx, "RESCUE ORGANIZATIONS — CONTACT PAWS ON RECORD TO HELP", S - 120, 1)[0], 60, y);

  ctx.fillStyle = "#dc2626"; ctx.fillRect(0, 896, S, 4);
  if (logo) ctx.drawImage(logo, 60, 924, 130, 130);
  ctx.fillStyle = "#fff"; ctx.font = `900 36px ${DISPLAY}`;
  ctx.fillText("PAWS ON RECORD", 215, 968);
  ctx.fillStyle = "#d4d4d8"; ctx.font = `600 22px ${DISPLAY}`;
  fitFont(ctx, "Calling for help — not the rescue org. Contact us:", 600, 22, DISPLAY, 620);
  ctx.fillText("Calling for help — not the rescue org. Contact us:", 215, 1004);
  const shortUrl = url.replace(/^https?:\/\//, "");
  ctx.fillStyle = "#f87171";
  fitFont(ctx, shortUrl, 700, 24, "'IBM Plex Mono', monospace", 620);
  ctx.fillText(shortUrl, 215, 1040);
  if (qr) {
    ctx.fillStyle = "#fff"; ctx.fillRect(860, 914, 160, 160);
    ctx.drawImage(qr, 865, 919, 150, 150);
  }
  return new Promise((resolve) => c.toBlob(resolve, "image/png"));
}
